import { createClient } from "@/app/utils/supabase/client";
import {
  QualityCheck,
  QualityCheckInsert,
  QualityCheckUpdate
} from "@/app/lib/supabase/types";
import { SUPABASE_TABLES } from "@/app/lib/supabase/supabaseDBConstants";

export const qualityChecksApi = {
  // Quality Check operations
  async getQualityChecks(companyId: string, limit?: number): Promise<QualityCheck[]> {
    const supabase = createClient();

    let query = supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKS)
      .select(`
        *,
        batch:batches(name),
        order:orders(name),
        production_stage:production_stages(name)
      `)
      .eq("company_id", companyId)
      .is("deleted_at", null)
      .order("updated_at", { ascending: false });

    // Apply limit if specified
    if (limit && limit > 0) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching quality checks:", error);
      throw error;
    }

    // Transform the data to include the names as flat properties
    return (data || []).map(check => ({
      ...check,
      batch_name: check.batch?.name || null,
      order_name: check.order?.name || null,
      production_stage_name: check.production_stage?.name || null,
      can_be_thumbnail_shown: check.is_public_for_thumbnail,
      // Remove the nested objects
      batch: undefined,
      order: undefined,
      production_stage: undefined,
    })) as QualityCheck[];
  },

  async getQualityChecksByProject(projectId: string, limit?: number): Promise<QualityCheck[]> {
    const supabase = createClient();

    let query = supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKS)
      .select(`
        *,
        batch:batches(name),
        order:orders(name),
        production_stage:production_stages(name)
      `)
      .eq("project_id", projectId)
      .is("deleted_at", null)
      .order("updated_at", { ascending: false });

    // Apply limit if specified
    if (limit && limit > 0) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching quality checks by project:", error);
      throw error;
    }

    // Transform the data to include the names as flat properties
    return (data || []).map(check => ({
      ...check,
      batch_name: check.batch?.name || null,
      order_name: check.order?.name || null,
      production_stage_name: check.production_stage?.name || null,
      can_be_thumbnail_shown: check.is_public_for_thumbnail,
      // Remove the nested objects
      batch: undefined,
      order: undefined,
      production_stage: undefined,
    })) as QualityCheck[];
  },

  async getQualityChecksByProductionStage(productionStageId: string, limit?: number): Promise<QualityCheck[]> {
    const supabase = createClient();

    let query = supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKS)
      .select(`
        *,
        batch:batches(name),
        order:orders(name),
        production_stage:production_stages(name)
      `)
      .eq("production_stage_id", productionStageId)
      .is("deleted_at", null)
      .order("updated_at", { ascending: false });

    // Apply limit if specified
    if (limit && limit > 0) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching quality checks by production stage:", error);
      throw error;
    }

    // Transform the data to include the names as flat properties
    return (data || []).map(check => ({
      ...check,
      batch_name: check.batch?.name || null,
      order_name: check.order?.name || null,
      production_stage_name: check.production_stage?.name || null,
      can_be_thumbnail_shown: check.is_public_for_thumbnail,
      // Remove the nested objects
      batch: undefined,
      order: undefined,
      production_stage: undefined,
    })) as QualityCheck[];
  },

  async getQualityCheck(qualityCheckId: string): Promise<QualityCheck> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKS)
      .select(`
        *,
        batch:batches(name),
        order:orders(name),
        production_stage:production_stages(name)
      `)
      .eq("id", qualityCheckId)
      .is("deleted_at", null)
      .single();

    if (error) {
      console.error("Error fetching quality check:", error);
      throw error;
    }

    if (!data) {
      throw new Error("Quality check not found");
    }

    // Transform the data to include the names as flat properties
    return {
      ...data,
      batch_name: data.batch?.name || null,
      order_name: data.order?.name || null,
      production_stage_name: data.production_stage?.name || null,
      can_be_thumbnail_shown: data.is_public_for_thumbnail,
      // Remove the nested objects
      batch: undefined,
      order: undefined,
      production_stage: undefined,
    } as QualityCheck;
  },

  async createQualityCheck(qualityCheck: QualityCheckInsert): Promise<QualityCheck> {
    const supabase = createClient();

    // Debug: Check if user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error("No authenticated user found when creating quality check");
      throw new Error("User must be authenticated to create quality checks");
    }

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKS)
      .insert(qualityCheck)
      .select("*")
      .single();

    if (error) {
      console.error("Error creating quality check:", error, qualityCheck, JSON.stringify(qualityCheck));
      console.error("User ID:", user.id);
      console.error("Company ID:", qualityCheck.company_id);
      throw error;
    }

    return data;
  },

  async updateQualityCheck(qualityCheckId: string, updates: QualityCheckUpdate): Promise<QualityCheck> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKS)
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq("id", qualityCheckId)
      .select("*")
      .single();

    if (error) {
      console.error("Error updating quality check:", error);
      throw error;
    }

    return data;
  },

  async deleteQualityCheck(qualityCheckId: string): Promise<void> {
    const supabase = createClient();

    const { error } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKS)
      .update({
        deleted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", qualityCheckId);

    if (error) {
      console.error("Error deleting quality check:", error);
      throw error;
    }
  },

  // Bulk operations
  async createQualityChecks(qualityChecks: QualityCheckInsert[]): Promise<QualityCheck[]> {
    const supabase = createClient();

    const { data, error } = await supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKS)
      .insert(qualityChecks)
      .select("*")

    if (error) {
      console.error("Error creating quality checks:", error);
      throw error;
    }

    return data || [];
  },

  // Utility function to get quality check statistics
  async getQualityCheckStats(filters: {
    companyId?: string;
    projectId?: string;
    productionStageId?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{
    total: number;
    passed: number;
    failed: number;
    na: number;
    passRate: number;
  }> {
    const supabase = createClient();

    let query = supabase
      .from(SUPABASE_TABLES.QUALITY_CHECKS)
      .select("status", { count: 'exact' })
      .is("deleted_at", null);

    if (filters.companyId) {
      query = query.eq("company_id", filters.companyId);
    }
    if (filters.projectId) {
      query = query.eq("project_id", filters.projectId);
    }
    if (filters.productionStageId) {
      query = query.eq("production_stage_id", filters.productionStageId);
    }
    if (filters.dateFrom) {
      query = query.gte("created_at", filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte("created_at", filters.dateTo);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error("Error fetching quality check stats:", error);
      throw error;
    }

    const checks = data || [];
    const total = count || 0;
    const passed = checks.filter(c => c.status === "passed").length;
    const failed = checks.filter(c => c.status === "failed").length;
    const na = checks.filter(c => c.status === "na").length;
    const passRate = total > 0 ? (passed / (passed + failed)) * 100 : 0;

    return {
      total,
      passed,
      failed,
      na,
      passRate,
    };
  },
};